import { useEffect, useMemo, useRef, useState } from 'react';
import type { RenditionSize } from '@shared/utils/photoRendition';
import { addRendition } from '@shared/utils/photoRendition';
import { LOW_RES_RENDITION, HIGH_RES_RENDITION, buildHighResSrcSet, buildLowResUrl } from './renditions';
import { reportImageLoadFailure } from '@shared/utils/reportImageLoadFailure';
import { withWindow } from '@shared/utils/withWindow';

// If the high-res image loads within this window, skip the crossfade transition.
// Matches the crossfade delay (150ms) — if the image is ready before the
// transition would start playing, there's no visual benefit to animating.
// This catches both memory cache (sync) and disk cache hits (304 revalidation
// adds a round-trip but is still perceptibly instant on production CDNs).
const IMMEDIATE_LOAD_GRACE_MS = 150;

// Browsers select a candidate ≈ sizes × devicePixelRatio and offer no way to cap DPR directly.
// On DPR-3 phones that over-selects (570px container → 1710 device px → the 2048 candidate,
// 2× the bytes of the visually-indistinguishable 1280). Standard workaround: under-report the
// CSS width by the excess ratio so sizes × realDPR lands on the DPR-2-equivalent candidate.
// SSR does not reach this memo (no consumer reports a positive measured width server-side); the SSR path's static sizes
// expressions accept the uncapped pick as a bounded over-fetch instead.
const MAX_EFFECTIVE_DPR = 2;

type ProgressiveImageState = Readonly<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  /**
   * True when the high-res image was available immediately or within the grace
   * window, so the UI can skip the crossfade transition.
   *
   * This is intentionally a UX signal ("was the load perceptibly instant?"),
   * not a literal "came from disk cache" signal.
   */
  isHighResFromCache: boolean;
  /** `srcSet` candidate list for the high-res layer (PRNT-4849). Undefined until
   *  `containerWidthPx` is known and rendition-based URLs are in use — see `containerWidthPx`. */
  highResSrcSet?: string;
  /** `sizes` paired with `highResSrcSet`, expressed as the DPR-2-capped container width.
   *  Undefined until `containerWidthPx` is measured — including in `skipPreload` mode, where the
   *  caller supplies its own fallback until this becomes available (eager mode: a static SSR
   *  expression; lazy mode: none — the browser's 100vw default — until its post-mount latch
   *  applies this measured value). */
  highResSizes?: string;
}>;

type UseProgressiveImageArgs = Readonly<{
  photoUrl: string;
  lowResRendition?: RenditionSize;
  highResRendition?: RenditionSize;
  /** Use the raw photoUrl for both layers (no rendition query). Honors the
   *  `guestSkipPagePhotoRendition` kill switch for environments that must not request renditioned
   *  page-photo URLs. Blur-up still works (blurred raw → sharp raw), just without renditions. */
  skipRendition?: boolean;

  /** Whether the high-res preload sets crossOrigin='anonymous'. Default true (legacy behavior).
   *  Pass false when the display path is non-CORS (CSS background-image / <img>) so the preload
   *  warms the same cache entry the display fetches — otherwise the low-res fades before the
   *  high-res paints (empty flash). See CoverPhoto. */
  preloadCrossOrigin?: boolean;

  /**
   * Real, measured container width in CSS px (e.g. from `useSizeObserver`). Only meaningful for
   * consumers that render a real `<img>` for the high-res layer (fixed-aspect/square, and the
   * uncropped branch in its lazySharpImg mode) — the remaining CSS background-image consumer
   * (viewport) must omit this, since `srcSet`/`sizes` have no effect on `background-image` and
   * would just make this hook test-load a candidate that the display layer never requests,
   * defeating the whole point of the cache-warming preload below. Default-mode uncropped
   * consumers also omit it deliberately: they stay fetch-identical to the legacy background
   * path (single xlarge URL), srcset being reserved for the lazySharpImg opt-in.
   *
   * When provided and > 0, this hook builds a `srcSet`/`sizes` pair (PRNT-4849) AND applies that
   * same pair to the offscreen preload `Image()` (not just a single `highResUrl`) so the browser's
   * responsive-image source-selection algorithm resolves the exact same candidate URL both times.
   * Skipping this on either side would mean the preload warms a different resource than the
   * on-page `<img>` ends up requesting — a silent double-fetch, and (worse) `isHighResLoaded`
   * would report "loaded" for the wrong image, letting the low-res layer fade out before the
   * actually-displayed candidate has painted.
   *
   * Falsy/omitted (e.g. not yet measured on first render) falls back to the original
   * single-URL `highResUrl` behavior — no regression, just no responsive srcset yet.
   */
  containerWidthPx?: number;

  /**
   * Self-loading <img> adoption mode, used by two callers in OptimizedEventGuestSitePhoto:
   * eager-SSR (sharp <img> in SSR markup with fetchpriority=high) and lazy (loading="lazy" in
   * SSR markup). In both, the on-page <img>'s own fetch is authoritative — constructing the
   * usual offscreen Image() here would double-fetch those exact bytes (eager) or defeat the
   * laziness entirely (lazy). So when true, this hook computes URLs only and performs NO
   * preload; the on-page <img>'s own onLoad / `complete` check (owned by
   * OptimizedEventGuestSitePhoto) drives the crossfade. isHighResLoaded stays false in this
   * mode — skipPreload consumers must not read it.
   *
   * `highResSizes` is NOT unconditionally suppressed in this mode: it reports undefined until
   * `containerWidthPx` is a real positive width, and the caller decides what reaches the
   * browser before then (eager: its static SSR sizes expression; lazy: nothing until its
   * post-mount latch). Once a real width is available, this hook reports the DPR-capped
   * measured value, which a static per-layout expression can't encode.
   */
  skipPreload?: boolean;
}>;

/**
 * Manages progressive image loading: computes low-res and high-res URLs,
 * preloads the high-res image in the background, and reports when it's ready.
 *
 * On SSR, URLs are computed synchronously (available in initial render).
 * The useEffect for preloading only fires on the client after hydration.
 *
 * Cache detection uses a two-tier approach:
 * 1. Synchronous: `img.complete && naturalWidth > 0` right after setting src
 *    catches memory cache hits (image decoded and available immediately).
 * 2. Grace timer: A short setTimeout checks if the image loaded within
 *    IMMEDIATE_LOAD_GRACE_MS — catches disk cache hits where the browser
 *    needed a few ms for disk I/O but the load was still perceptibly instant.
 *
 * If the image hasn't loaded by the grace timer, onload eventually fires and
 * marks it loaded WITHOUT the cache flag, so the full crossfade plays.
 */
export const useProgressiveImage = ({
  photoUrl,
  lowResRendition = LOW_RES_RENDITION,
  highResRendition = HIGH_RES_RENDITION,
  skipRendition = false,
  preloadCrossOrigin = true,
  containerWidthPx,
  skipPreload = false,
}: UseProgressiveImageArgs): ProgressiveImageState => {
  // lowResRendition is honored only when a caller overrides it (e.g. quality-pinned galleries);
  // the default 'tiny' path goes through the dialect builder so asset-optimizer URLs get a real
  // placeholder (resize=w48) instead of an ignored rendition param.
  const lowResUrl = useMemo(() => {
    if (skipRendition) return photoUrl;
    if (lowResRendition !== LOW_RES_RENDITION) return addRendition({ url: photoUrl, renditionSize: lowResRendition });
    return buildLowResUrl(photoUrl);
  }, [photoUrl, lowResRendition, skipRendition]);

  const highResUrl = useMemo(() => (skipRendition ? photoUrl : addRendition({ url: photoUrl, renditionSize: highResRendition })), [photoUrl, highResRendition, skipRendition]);

  // Gated on a real measured width (not just "provided") so a not-yet-measured container doesn't
  // momentarily advertise a nonsense `sizes` and steer candidate selection with it.
  // The check is `> 0`, NOT truthiness: 0 is not the only unmeasured value that arrives here.
  // PhotoTrio passes `(containerDimensions.width - GAP * 2) / 3`, which is NEGATIVE (-2.67) on the
  // render before useSizeObserver reports — and a negative is truthy. Per the HTML spec a negative
  // source-size is invalid, so the browser drops the entry and falls back to the 100vw default,
  // i.e. the largest candidate rather than the per-layout estimate. `> 0` also rejects NaN.
  // Once measurement lands, this recomputes and the <img> picks up the correct pair.
  // srcSet gating: measured width is required only when this hook runs the preload itself
  // (the offscreen Image must resolve the same candidate as the on-page <img>, which needs
  // real sizes). In skipPreload mode the component owns sizes (static SSR expression), so the
  // candidate list is buildable immediately.
  const highResSrcSet = useMemo(
    () => (!skipRendition && (skipPreload || (containerWidthPx ?? 0) > 0) ? buildHighResSrcSet(photoUrl) : undefined),
    [photoUrl, skipRendition, containerWidthPx, skipPreload],
  );

  // skipPreload does not force this to undefined: SSR/first render has containerWidthPx === 0
  // (unmeasured), so this still falls through the gate below and the component's static ssrSizes
  // is what ships in the initial markup — but once useSizeObserver measures a real width, this
  // reports the same DPR-capped value non-eager consumers get, reflecting the per-event crop
  // scale-up that a static per-layout expression can't encode.
  const highResSizes = useMemo(() => {
    // Narrowed to a local rather than testing `(containerWidthPx ?? 0) > 0` inline: that form
    // guards correctly but does not narrow `containerWidthPx` itself, so the multiply below stays
    // possibly-undefined to TS.
    const measuredWidthPx = containerWidthPx ?? 0;
    if (!highResSrcSet || !(measuredWidthPx > 0)) return undefined;
    // withWindow, not a bare `window` read: the guest site also renders inside the admin preview
    // iframe, where the frame's own devicePixelRatio is the one that governs candidate selection.
    const dpr = withWindow(win => win.devicePixelRatio, 1) || 1;
    const dprScale = dpr > MAX_EFFECTIVE_DPR ? MAX_EFFECTIVE_DPR / dpr : 1;
    return `${Math.round(measuredWidthPx * dprScale)}px`;
  }, [highResSrcSet, containerWidthPx]);

  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  const [isHighResFromCache, setIsHighResFromCache] = useState(false);

  const highResImageRef = useRef<HTMLImageElement | null>(null);

  // "Latest value" refs so the load/reset effect below (which intentionally does NOT depend on
  // highResSrcSet/highResSizes — see its comment) still reads the current measured-width-derived
  // values when it does run for a genuine highResUrl change, instead of a stale closure value.
  const highResSrcSetRef = useRef(highResSrcSet);
  const highResSizesRef = useRef(highResSizes);
  highResSrcSetRef.current = highResSrcSet;
  highResSizesRef.current = highResSizes;

  useEffect(() => {
    if (skipPreload) return; // eager/lazy self-load mode: the on-page <img> owns loading; no offscreen preload

    if (!highResUrl) {
      setIsHighResLoaded(false);
      setIsHighResFromCache(false);
      return;
    }

    let isCancelled = false;

    // Reset state for the new URL.
    setIsHighResLoaded(false);
    setIsHighResFromCache(false);

    const img = new Image();
    // Browsers cache CORS and non-CORS responses under separate keys. When the display path is
    // non-CORS (CSS background-image / <img>), a crossOrigin preload warms a different cache entry
    // than the display fetches, so the low-res fades before the high-res paints (empty flash) —
    // those consumers pass preloadCrossOrigin={false}. Default stays true for legacy behavior; the
    // canvas fallback loads its own CORS image regardless.
    if (preloadCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    highResImageRef.current = img;

    const startedAt = performance.now();

    const markLoaded = (fromCacheLike: boolean) => {
      if (isCancelled) return;
      setIsHighResLoaded(true);
      setIsHighResFromCache(fromCacheLike);
    };

    // Use elapsed time to determine if the load was perceptibly instant.
    // This handles all cache scenarios (memory + disk) without a race
    // between onload and the grace timer.
    img.onload = () => {
      const loadedWithinGrace = performance.now() - startedAt <= IMMEDIATE_LOAD_GRACE_MS;
      markLoaded(loadedWithinGrace);
    };

    // On error, leave state unchanged — the blurred low-res layer stays visible.
    // Setting isHighResLoaded would hide the low-res layer and show a blank
    // (since the high-res src is only set when loaded). Blurred low-res is
    // better than nothing. Log to New Relic so we can track CDN failures.
    img.onerror = () => {
      reportImageLoadFailure({ component: 'HighResPreload', src: img.currentSrc || highResUrl });
    };

    // When a responsive candidate list is active, set srcset/sizes on the offscreen preloader
    // too — the browser then applies the same source-selection algorithm here as it will for the
    // real on-page <img> (same srcSet/sizes strings, same viewport), so this test-load resolves
    // to the identical URL. `img.src` still gets set as a plain fallback for engines that ignore
    // srcset — modern browsers prefer srcset+sizes over `src` whenever both are present.
    // Read from the refs (not the outer closure values) so this initial set always reflects the
    // latest measured width even though this effect intentionally doesn't depend on them below —
    // see the follow-up effect for why.
    if (highResSrcSetRef.current) {
      img.srcset = highResSrcSetRef.current;
    }
    if (highResSizesRef.current) {
      img.sizes = highResSizesRef.current;
    }
    img.src = highResUrl;

    // Synchronous memory cache hit — img.complete is true immediately after
    // setting src. onload won't fire for these, so handle it here.
    if (img.complete && img.naturalWidth > 0) {
      markLoaded(true);
    }

    return () => {
      isCancelled = true;
      img.onload = null;
      img.onerror = null;
      highResImageRef.current = null;
    };
    // Intentionally NOT depending on highResSrcSet/highResSizes: those change on every resize
    // (e.g. mobile orientation change) once containerWidthPx updates, and re-running this effect
    // resets isHighResLoaded/isHighResFromCache to false unconditionally. If the already-loaded
    // photo is sharp and on screen, a resize must not revert it to the blurred placeholder while
    // a differently-sized candidate re-fetches — the follow-up effect below handles keeping the
    // offscreen preloader in sync with the current srcSet/sizes without resetting visible state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highResUrl, preloadCrossOrigin, skipPreload]);

  // Keeps the offscreen preloader's srcset/sizes in sync with the container's current measured
  // width (e.g. after a resize or orientation change), independent of the load/reset effect above.
  // Setting `srcset`/`sizes` on an existing Image() makes the browser re-run its native responsive
  // source-selection algorithm and fetch a better-matching candidate in the background if needed —
  // without us forcing isHighResLoaded back to false, so the already-displayed sharp photo (from
  // whichever candidate loaded first) stays visible the whole time. `img.onload` is still attached,
  // so `markLoaded` will run again if the browser does swap to a new resource, refreshing
  // isHighResFromCache's usual best-effort read on that fetch.
  useEffect(() => {
    if (skipPreload) return; // eager/lazy self-load mode: the on-page <img> owns loading; no offscreen preload

    const img = highResImageRef.current;
    if (!img) return;
    if (highResSrcSet) {
      img.srcset = highResSrcSet;
    }
    if (highResSizes) {
      img.sizes = highResSizes;
    }
    // Mirrors the synchronous-cache-hit check in the load/reset effect above: onload doesn't
    // reliably refire when the new srcset candidate resolves synchronously from cache.
    if (img.complete && img.naturalWidth > 0) {
      setIsHighResLoaded(true);
      setIsHighResFromCache(true);
    }
  }, [highResSrcSet, highResSizes, skipPreload]);

  return {
    lowResUrl,
    highResUrl,
    isHighResLoaded,
    isHighResFromCache,
    highResSrcSet,
    highResSizes,
  };
};
